import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Toast, Dialog, DotLoading } from 'antd-mobile';
import { LeftOutline, PhoneFill } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  buildGoogleMapsNavUrl,
  useRealtimeChannel,
  DRIVER_VISIBLE_TRIP_OR_FILTER,
  assertDepartAllowed,
  getSignedUrl,
  setAcceptingTasks,
  getCurrentPositionCoords,
} from '@ubus/shared';
import type { Trip } from '@ubus/shared';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import GPSManager, { GPS_POST_ARRIVAL_DELAY_MS } from '../utils/GPSManager';
import { useAuth } from '../hooks/useAuth';
import TaskDetailMap from '../components/TaskDetailMap';
import {
  extractPlaceName,
  formatDepartureDateTime,
  formatEstimatedArrival,
  formatPassengerCapacity,
  formatTripVehicleLabel,
  getDetailPhase,
} from '../utils/tripUi';
import {
  appendConfirmedTrip,
  fetchTodayShiftInspection,
  inspectionTodayLocal,
  toGateRecord,
} from '../utils/inspectionApi';
import type { VehicleInspection } from '@ubus/shared';

const TRIP_STATE_KEY = 'ubus-trip-state';

interface TripStateEntry {
  gpsReporting: boolean;
  tripStarted: boolean;
  departedAt?: string;
  gpsStopAt?: number;
}

interface TripState {
  [tripId: string]: TripStateEntry;
}

function getTripState(tripId: string): TripStateEntry {
  try {
    const raw = localStorage.getItem(TRIP_STATE_KEY);
    if (!raw) return { gpsReporting: false, tripStarted: false };
    const states: TripState = JSON.parse(raw);
    return states[tripId] || { gpsReporting: false, tripStarted: false };
  } catch {
    return { gpsReporting: false, tripStarted: false };
  }
}

function saveTripState(tripId: string, state: TripStateEntry) {
  try {
    const raw = localStorage.getItem(TRIP_STATE_KEY);
    const states: TripState = raw ? JSON.parse(raw) : {};
    states[tripId] = { ...states[tripId], ...state };
    localStorage.setItem(TRIP_STATE_KEY, JSON.stringify(states));
  } catch {
    // ignore
  }
}

function isGpsEnabled(): boolean {
  return localStorage.getItem('ubus_gps_enabled') !== 'false';
}

function TaskDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>([]);
  const [attachmentLoadError, setAttachmentLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gpsReporting, setGpsReporting] = useState(false);
  const [departedAt, setDepartedAt] = useState<string | null>(null);
  const [shiftInspection, setShiftInspection] = useState<VehicleInspection | null>(null);
  const { driverId } = useAuth();
  const gpsManagerRef = useRef<GPSManager | null>(null);

  const refreshShiftInspection = useCallback(async () => {
    if (!driverId) {
      setShiftInspection(null);
      return;
    }
    const insp = await fetchTodayShiftInspection(driverId);
    setShiftInspection(insp);
  }, [driverId]);

  useEffect(() => {
    void refreshShiftInspection();
  }, [refreshShiftInspection]);

  useEffect(() => {
    if (id) {
      const state = getTripState(id);
      setGpsReporting(state.gpsReporting);
      setDepartedAt(state.departedAt || null);
    }
  }, [id]);

  const stopGPSReporting = useCallback(() => {
    gpsManagerRef.current?.stopReporting();
    gpsManagerRef.current = null;
    setGpsReporting(false);
    if (id) saveTripState(id, { gpsReporting: false, tripStarted: false });
  }, [id]);

  const autoStartGPSReporting = useCallback(async (showToast = false) => {
    if (!id || !driverId) return;
    if (!isGpsEnabled()) return;
    if (gpsManagerRef.current?.getReportingStatus()) return;

    const manager = new GPSManager(id, driverId);
    gpsManagerRef.current = manager;
    const started = await manager.startReporting();
    if (started) {
      setGpsReporting(true);
      const existing = getTripState(id);
      saveTripState(id, { gpsReporting: true, tripStarted: existing.tripStarted, departedAt: existing.departedAt });
      if (showToast) {
        Toast.show({ content: t('task.gpsStarted') });
      }
    } else {
      gpsManagerRef.current = null;
    }
  }, [id, driverId]);

  const handleTrackingActiveChange = useCallback(
    (updated: Pick<Trip, 'status' | 'tracking_active'>) => {
      if (updated.tracking_active && updated.status === 'dispatched') {
        void autoStartGPSReporting(true);
        return;
      }
      if (!updated.tracking_active && updated.status !== 'in_progress') {
        stopGPSReporting();
      }
    },
    [autoStartGPSReporting, stopGPSReporting]
  );

  useRealtimeChannel(
    id ? `trip-detail-${id}` : 'trip-detail-none',
    id
      ? [
          {
            event: 'UPDATE',
            table: 'trips',
            filter: `id=eq.${id}`,
            callback: (payload) => {
              const updated = payload.new as Trip;
              setTrip((prev) => (prev ? { ...prev, ...updated } : updated));
              handleTrackingActiveChange(updated);
            },
          },
        ]
      : [],
    [id, handleTrackingActiveChange]
  );

  const fetchTripDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('trips')
        .select('*, vehicles(*), drivers(*), organizations(*)')
        .eq('id', id)
        .single();

      if (error) {
        Toast.show({ icon: 'fail', content: error.message });
      } else {
        setTrip(data);
        if (data.status === 'cancelled') {
          stopGPSReporting();
        } else if (data.status === 'completed') {
          const pendingStop =
            gpsManagerRef.current?.hasScheduledStop() ||
            (id && localStorage.getItem(`ubus-gps-stop-at-${id}`));
          if (pendingStop) {
            setGpsReporting(true);
          } else {
            stopGPSReporting();
          }
        } else if (data.status === 'dispatched' && data.tracking_active) {
          void autoStartGPSReporting(false);
        } else if (!data.tracking_active && data.status !== 'in_progress') {
          stopGPSReporting();
        }
      }
    } finally {
      setLoading(false);
    }
  }, [id, stopGPSReporting, autoStartGPSReporting]);

  useEffect(() => {
    if (id) fetchTripDetail();
  }, [id, fetchTripDetail]);

  useEffect(() => {
    let cancelled = false;
    const paths = trip?.attachment_urls ?? [];
    if (!paths.length) {
      setAttachmentUrls([]);
      setAttachmentLoadError(false);
      return;
    }
    void (async () => {
      const urls: string[] = [];
      let failed = 0;
      for (const path of paths) {
        if (path.startsWith('http')) {
          urls.push(path);
          continue;
        }
        const signed = await getSignedUrl('uploads', path, 3600);
        if (signed) urls.push(signed);
        else failed += 1;
      }
      if (!cancelled) {
        setAttachmentUrls(urls);
        setAttachmentLoadError(failed > 0 && urls.length === 0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trip?.id, trip?.attachment_urls]);

  useEffect(() => {
    if (id && driverId && gpsReporting && !gpsManagerRef.current) {
      const manager = new GPSManager(id, driverId);
      gpsManagerRef.current = manager;
      manager.startReporting();
    }
  }, [id, driverId, gpsReporting]);

  useEffect(() => {
    return () => {
      if (gpsManagerRef.current?.hasScheduledStop()) {
        gpsManagerRef.current = null;
        return;
      }
      gpsManagerRef.current?.stopReporting();
      gpsManagerRef.current = null;
    };
  }, [id]);

  const startGPSReporting = async () => {
    if (!id || !driverId) return false;
    if (!isGpsEnabled()) {
      Toast.show({ icon: 'fail', content: t('task.gpsDisabled') });
      return false;
    }
    try {
      const manager = new GPSManager(id, driverId);
      gpsManagerRef.current = manager;
      const started = await manager.startReporting();
      if (!started) {
        gpsManagerRef.current = null;
        Toast.show({ icon: 'fail', content: t('task.gpsPermission') });
        return false;
      }
      setGpsReporting(true);
      const now = new Date().toISOString();
      setDepartedAt(now);
      saveTripState(id, { gpsReporting: true, tripStarted: true, departedAt: now });
      return true;
    } catch (err: unknown) {
      Toast.show({ icon: 'fail', content: err instanceof Error ? err.message : t('task.gpsStartFailed') });
      return false;
    }
  };

  const handleStartTrip = async () => {
    // Stop shift GPS before switching to trip GPS
    GPSManager.stopShiftReporting();

    const { error } = await supabase
      .from('trips')
      .update({ status: 'in_progress', updated_at: new Date().toISOString() })
      .eq('id', id!);

    if (error) {
      Toast.show({ icon: 'fail', content: error.message });
      return;
    }

    const gpsOk = await startGPSReporting();
    if (!gpsOk) {
      Toast.show({
        icon: 'fail',
        content: t('task.tripStartedGpsFailed'),
      });
    }
    fetchTripDetail();
  };

  const promptAfterReminder = () => {
    Dialog.show({
      title: t('task.endShiftTitle'),
      content: t('task.endShiftBody'),
      closeOnAction: true,
      actions: [
        [
          { key: 'later', text: t('common.later') },
          {
            key: 'go',
            text: t('task.fillAfterNow'),
            bold: true,
            onClick: () => navigate(`/inspection?mode=after&return=/task/${id}`),
          },
        ],
      ],
    });
  };

  const promptAcceptTasks = (vehicleId: string | null) => {
    Dialog.show({
      title: t('task.acceptTasksTitle'),
      content: t('task.acceptTasksBody'),
      closeOnAction: true,
      actions: [
        [
          {
            key: 'no',
            text: t('task.acceptTasksNo'),
            onClick: async () => {
              try {
                await setAcceptingTasks({
                  accept: false,
                  vehicleId,
                });
              } catch {
                // ignore
              }
              promptAfterReminder();
            },
          },
          {
            key: 'yes',
            text: t('task.acceptTasksYes'),
            bold: true,
            onClick: async () => {
              try {
                const coords = await getCurrentPositionCoords();
                await setAcceptingTasks({
                  accept: true,
                  vehicleId,
                  lat: coords.lat,
                  lng: coords.lng,
                });
                Toast.show({ icon: 'success', content: t('hall.acceptOn') });
              } catch (err) {
                const msg = err instanceof Error ? err.message : '';
                if (msg.includes('OPEN_TRIPS')) {
                  Toast.show({ content: t('hall.acceptBlockedOpenTrips') });
                } else if (msg.includes('INSPECTION') || msg.includes('INSPECTION_REQUIRED')) {
                  Toast.show({ content: t('hall.acceptBlockedInspection') });
                } else if (msg.includes('GPS') || msg.includes('Geolocation')) {
                  Toast.show({ content: t('hall.acceptGpsRequired') });
                } else {
                  Toast.show({ content: t('hall.acceptFailed') });
                }
              }
              promptAfterReminder();
            },
          },
        ],
      ],
    });
  };

  const handleCompleteTrip = async () => {
    const completedVehicleId = trip?.vehicle_id ?? null;
    const { error } = await supabase
      .from('trips')
      .update({ status: 'completed', updated_at: new Date().toISOString() })
      .eq('id', id!);

    if (error) {
      Toast.show({ icon: 'fail', content: error.message });
      return;
    }

    const stopAt = Date.now() + GPS_POST_ARRIVAL_DELAY_MS;
    if (gpsManagerRef.current) {
      gpsManagerRef.current.scheduleStopAfter(GPS_POST_ARRIVAL_DELAY_MS);
    } else if (id && driverId) {
      const manager = new GPSManager(id, driverId);
      gpsManagerRef.current = manager;
      const started = await manager.startReporting();
      if (started) {
        manager.scheduleStopAfter(GPS_POST_ARRIVAL_DELAY_MS);
      }
    }

    setGpsReporting(true);
    if (id) {
      saveTripState(id, {
        gpsReporting: true,
        tripStarted: true,
        departedAt: departedAt ?? undefined,
        gpsStopAt: stopAt,
      });
    }

    Toast.show({
      icon: 'success',
      content: t('task.tripCompletedGps'),
    });
    fetchTripDetail();
    promptAcceptTasks(completedVehicleId);
  };

  const showContactDialog = () => {
    const phone =
      trip?.contact_phone?.trim() || trip?.organizations?.contact_phone?.trim();
    if (!phone) {
      Toast.show({ content: t('task.noClientPhone') });
      return;
    }
    Dialog.show({
      title: t('task.contactCustomer'),
      content: phone,
      closeOnAction: true,
      actions: [
        [
          { key: 'cancel', text: t('common.cancel') },
          {
            key: 'call',
            text: t('common.call'),
            bold: true,
            onClick: () => {
              window.location.href = `tel:${phone}`;
            },
          },
        ],
      ],
    });
  };

  const handleDepart = async () => {
    if (!driverId || !id) return;

    const latest = await fetchTodayShiftInspection(driverId);
    setShiftInspection(latest);
    const gate = assertDepartAllowed({
      today: inspectionTodayLocal(),
      tripId: id,
      shiftInspection: toGateRecord(latest),
    });

    if (!gate.allowed && gate.reason === 'no_shift_inspection') {
      Dialog.show({
        title: t('task.checkRequiredTitle'),
        content: t('task.checkRequiredBody'),
        closeOnAction: true,
        actions: [
          [
            { key: 'cancel', text: t('common.cancel') },
            {
              key: 'inspect',
              text: t('hall.inspect'),
              bold: true,
              onClick: () => navigate(`/inspection?return=/task/${id}`),
            },
          ],
        ],
      });
      return;
    }

    if (!gate.allowed && (gate.reason === 'not_declared' || gate.reason === 'blocked_severity' || gate.reason === 'fitness_incomplete')) {
      Dialog.show({
        title: t('task.cannotSetOffTitle'),
        content:
          gate.reason === 'blocked_severity'
            ? t('task.criticalFailBody')
            : gate.reason === 'fitness_incomplete'
              ? t('task.fitnessRequiredBody')
              : t('task.finishCheckBody'),
        closeOnAction: true,
        actions: [
          [
            { key: 'cancel', text: t('common.cancel') },
            {
              key: 'inspect',
              text: t('hall.inspect'),
              bold: true,
              onClick: () => navigate(`/inspection?return=/task/${id}`),
            },
          ],
        ],
      });
      return;
    }

    const proceedDepart = async () => {
      if (latest && gate.inspectionId) {
        const ok = await appendConfirmedTrip(
          gate.inspectionId,
          id,
          latest.confirmed_trip_ids ?? []
        );
        if (!ok) {
          Toast.show({ icon: 'fail', content: t('task.saveConfirmFailed') });
          return;
        }
      }
      await handleStartTrip();
    };

    if (gate.reason === 'light_confirm_required') {
      const handler = Dialog.show({
        title: t('task.stillOkTitle'),
        content: t('task.stillOkBody'),
        closeOnAction: true,
        actions: [
          [
            {
              key: 'go',
              text: t('task.setOff'),
              bold: true,
              onClick: () => {
                void proceedDepart();
              },
            },
          ],
          [
            {
              key: 'recheck',
              text: t('task.recheck'),
              onClick: () => {
                handler.close();
                navigate(`/inspection?return=/task/${id}`);
              },
            },
          ],
          [{ key: 'cancel', text: t('common.cancel') }],
        ],
      });
      return;
    }

    // Already light-confirmed for this trip — depart without a second confirm dialog
    await proceedDepart();
  };

  const handleArrive = () => {
    Dialog.show({
      title: t('task.arriveTitle'),
      content: t('task.arriveBody'),
      closeOnAction: true,
      actions: [
        [
          { key: 'cancel', text: t('common.cancel') },
          { key: 'arrive', text: t('task.arrive'), bold: true, onClick: handleCompleteTrip },
        ],
      ],
    });
  };

  /** Disable Set off until a valid declared inspection exists (light-confirm still allowed via click). */
  const inspectionBlocksDepart = useMemo(() => {
    if (!id) return true;
    const gate = assertDepartAllowed({
      today: inspectionTodayLocal(),
      tripId: id,
      shiftInspection: toGateRecord(shiftInspection),
      lightConfirmedForTrip: true,
    });
    return !gate.allowed;
  }, [id, shiftInspection]);

  if (loading) {
    return (
      <div className="driver-detail-loading">
        <DotLoading color="primary" />
      </div>
    );
  }

  if (!trip) {
    return <div className="driver-detail-empty">{t('task.notFound')}</div>;
  }

  const phase = getDetailPhase(trip);
  const showFooter = phase === 'ready' || phase === 'in_progress' || phase === 'not_yet';
  const contactName =
    trip.contact_person?.trim() ||
    trip.organizations?.contact_person ||
    t('common.contact');
  const contactOrg = trip.organizations?.name || '';
  const vehicleLabel = formatTripVehicleLabel(trip, t);
  const actualDeparture = trip.started_at
    ? dayjs(trip.started_at).format('YYYY-MM-DD HH:mm')
    : departedAt
      ? dayjs(departedAt).format('YYYY-MM-DD HH:mm')
      : '—';
  const actualCompletion = trip.completed_at
    ? dayjs(trip.completed_at).format('YYYY-MM-DD HH:mm')
    : '—';
  const showActualTimes =
    phase === 'in_progress' ||
    trip.status === 'completed' ||
    Boolean(trip.started_at) ||
    Boolean(trip.completed_at);

  return (
    <div className={`driver-detail ${showFooter ? 'driver-detail--with-footer' : ''}`}>
      <header className="driver-detail-nav">
        <button type="button" className="driver-detail-nav-back" onClick={() => navigate(-1)} aria-label={t('common.back')}>
          <LeftOutline fontSize={22} />
        </button>
        <h1 className="driver-detail-nav-title">{t('task.details')}</h1>
        <span className="driver-detail-nav-spacer" aria-hidden="true" />
      </header>

      <TaskDetailMap
        tripId={trip.id}
        phase={phase}
        pickupGeopoint={trip.pickup_geopoint}
        dropoffGeopoint={trip.dropoff_geopoint}
        pickupAddress={trip.pickup_address}
        dropoffAddress={trip.dropoff_address}
      />

      <div className="driver-detail-status-strip">
        {phase === 'in_progress' ? (
          <>
            <span className="driver-detail-status-label">{t('task.estimatedArrival')}</span>
            <span className="driver-detail-status-value">
              {t('common.before', { time: formatEstimatedArrival(trip) })}
            </span>
          </>
        ) : (
          <>
            <span className="driver-detail-status-label">{t('task.plannedDeparture')}</span>
            <span className="driver-detail-status-value">{formatDepartureDateTime(trip)}</span>
          </>
        )}
      </div>

      <div className="driver-detail-body">
        <section className="driver-detail-section">
          <h2 className="driver-detail-section-title">{t('task.itinerary')}</h2>
          <div className="driver-route driver-route--detail">
            <div className="driver-route-dot driver-route-dot--blue" />
            <div>
              <div className="driver-route-place">{extractPlaceName(trip.pickup_address, t)}</div>
              <button
                type="button"
                className="driver-route-text driver-route-text--link"
                onClick={() => window.open(buildGoogleMapsNavUrl(trip.pickup_address), '_blank')}
              >
                {trip.pickup_address}
              </button>
            </div>
            <div className="driver-route-line" />
            <div />
            <div className="driver-route-dot driver-route-dot--green" />
            <div>
              <div className="driver-route-place">{extractPlaceName(trip.dropoff_address, t)}</div>
              <button
                type="button"
                className="driver-route-text driver-route-text--link"
                onClick={() => window.open(buildGoogleMapsNavUrl(trip.dropoff_address), '_blank')}
              >
                {trip.dropoff_address}
              </button>
            </div>
          </div>
        </section>

        <section className="driver-detail-section">
          <h2 className="driver-detail-section-title">{t('task.details')}</h2>
          {trip.trip_number ? (
            <DetailRow label={t('task.tripNumber')} value={trip.trip_number} />
          ) : null}
          <DetailRow label={t('common.vehicle')} value={vehicleLabel} />
          <DetailRow label={t('task.taskType')} value={t(`task.source.${trip.source || 'hq'}`)} />
          <DetailRow label={t('task.passengers')} value={formatPassengerCapacity(trip)} />
          {phase === 'in_progress' ? (
            <DetailRow
              label={t('task.estimatedArrival')}
              value={formatEstimatedArrival(trip)}
            />
          ) : (
            <DetailRow label={t('task.plannedDeparture')} value={formatDepartureDateTime(trip)} />
          )}
        </section>

        {showActualTimes ? (
          <section className="driver-detail-section">
            <h2 className="driver-detail-section-title">{t('task.trajectory')}</h2>
            <DetailRow
              label={t('task.actualDeparture')}
              value={actualDeparture}
              highlight={phase === 'in_progress'}
            />
            {trip.status === 'completed' || trip.completed_at ? (
              <DetailRow label={t('task.actualCompletion')} value={actualCompletion} />
            ) : null}
          </section>
        ) : null}

        {trip.organizations && (
          <section className="driver-detail-section">
            <h2 className="driver-detail-section-title">{t('task.contactPerson')}</h2>
            <div className="driver-detail-contact">
              <div className="driver-detail-contact-avatar">
                {contactName.charAt(0).toUpperCase()}
              </div>
              <div className="driver-detail-contact-info">
                <div className="driver-detail-contact-name">{contactName}</div>
                {contactOrg && (
                  <div className="driver-detail-contact-org">{contactOrg}</div>
                )}
              </div>
              <button
                type="button"
                className="driver-detail-contact-phone"
                aria-label={t('task.contactCustomerAria')}
                onClick={showContactDialog}
              >
                <PhoneFill fontSize={20} />
              </button>
            </div>
          </section>
        )}

        {trip.special_notes && (
          <div className="driver-detail-notes">{t('task.notesPrefix', { notes: trip.special_notes })}</div>
        )}

        {((trip.attachment_urls?.length ?? 0) > 0 || attachmentUrls.length > 0) && (
          <div className="driver-detail-attachments">
            <div className="driver-detail-attachments__title">{t('task.tripPhotos')}</div>
            {attachmentLoadError ? (
              <div className="driver-detail-attachments__empty">{t('task.unableLoadPhotos')}</div>
            ) : attachmentUrls.length === 0 ? (
              <div className="driver-detail-attachments__empty">{t('task.loadingPhotos')}</div>
            ) : (
              <div className="driver-detail-attachments__grid">
                {attachmentUrls.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt={t('task.tripAttachmentAlt')} className="driver-detail-attachments__img" />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {trip.status === 'cancelled' && (
          <div className="driver-detail-cancelled">
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('task.cancelledTitle')}</div>
            <div>
              {t('task.cancelledReason')}: {trip.cancelled_reason?.trim() || '—'}
            </div>
            <div>
              {t('task.cancelledAt')}:{' '}
              {trip.cancelled_at
                ? dayjs(trip.cancelled_at).format('YYYY-MM-DD HH:mm')
                : '—'}
            </div>
          </div>
        )}
      </div>

      {showFooter && (
        <div className="driver-detail-footer">
          {phase === 'ready' && inspectionBlocksDepart && (
            <div style={{ marginBottom: 8, fontSize: 13, color: '#c0392b', textAlign: 'center' }}>
              {t('task.checkRequiredBody')}
            </div>
          )}
          {phase === 'ready' && (
            inspectionBlocksDepart ? (
              <button
                type="button"
                className="driver-detail-btn driver-detail-btn--primary"
                onClick={() => navigate(`/inspection?return=/task/${id}`)}
              >
                {t('hall.inspect')}
              </button>
            ) : (
              <button type="button" className="driver-detail-btn driver-detail-btn--primary" onClick={() => void handleDepart()}>
                {t('task.setOff')}
              </button>
            )
          )}
          {phase === 'in_progress' && (
            <button type="button" className="driver-detail-btn driver-detail-btn--success" onClick={handleArrive}>
              {t('task.arrive')}
            </button>
          )}
          {phase === 'not_yet' && (
            <button type="button" className="driver-detail-btn driver-detail-btn--disabled" disabled>
              {t('task.notYetTime')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DetailRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="driver-detail-row">
      <span className="driver-detail-row-label">{label}</span>
      <span className={`driver-detail-row-value ${highlight ? 'driver-detail-row-value--link' : ''}`}>
        {value}
      </span>
    </div>
  );
}

export default TaskDetail;
